import React, { useMemo } from 'react';
import { AppButton, Box, Hint, InfoOutlineIcon, Inscription, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { HorizontalLine } from '@/components';
import ManyItemsTooltip from '@/components/elements/ManyItemsTooltip/ManyItemsTooltip';
import {
  ConnectToOptions,
  PayrollCalendarFrequency,
} from '@/modules/employer/components/SetupEmployer/Features/PayrollCalendar/payrollCalendar.types';
import { PayrollCalendar } from '@/modules/Renewals/renewals.types';
import theme from '@/styles/theme';

import { getScheduleDaySummary } from './utils/getScheduleDaySummary';
import { groupScheduleByYearMonth } from './utils/groupScheduleByYearMonth';
import PayrollCalendarScheduleItem from './PayrollCalendarScheduleItem';

const StyledInfoIcon = styled(InfoOutlineIcon)`
  &:hover {
    fill: ${({ theme }) => theme.colors.iconAccent};
  }
`;

interface Props {
  calendar: PayrollCalendar;
  linkedPlanNames?: string[];
  onEdit?: (calendarId: string) => void;
  testId?: string;
}

const PayrollCalendarCard: React.FC<Props> = ({ calendar, linkedPlanNames, onEdit, testId }) => {
  const scheduleYears = useMemo(() => groupScheduleByYearMonth(calendar.schedule), [calendar.schedule]);

  const linkedToText = linkedPlanNames?.length ? linkedPlanNames.join(', ') : '-';
  const isDefaultCalendar = calendar.groupType === ConnectToOptions.ALL;
  const rawFrequency = calendar.config?.frequency || calendar.frequencyType;
  const frequency = rawFrequency
    ? PayrollCalendarFrequency[rawFrequency as keyof typeof PayrollCalendarFrequency] || rawFrequency
    : '-';

  const annualDate = calendar.schedule?.[0] ? dayjs(calendar.schedule[0]).format(DEFAULT_DATE_FORMAT) : '-';
  const { label: scheduleDayLabel, value: scheduleDayValue } = getScheduleDaySummary(
    rawFrequency,
    calendar.config,
    annualDate
  );

  const startDate = calendar.config?.startDate
    ? dayjs(calendar.config.startDate).format(DEFAULT_DATE_FORMAT)
    : '-';

  return (
    <Box
      data-testid={testId}
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      border={{ size: 'small', color: 'border1' }}
      round="container1Round"
      background="canvas"
    >
      {/* Calendar Name */}
      <Inscription weight="bold" size="16px" lineHeight="22px" color="textBody">
        {calendar.name || 'Unnamed Calendar'}
      </Inscription>

      {/* Linked to / Default Calendar */}
      {isDefaultCalendar ? (
        <Box direction="row" align="center" gap="spacing4" margin={{ top: 'spacing4' }}>
          <Text weight={700} color="textBody" data-testid={`${testId}_linked-to`}>
            Default Calendar
          </Text>
          <Hint
            hintAlign="vertical"
            hintHeight={78}
            hintWidth={194}
            hintElement={<StyledInfoIcon color="iconSecondary" size="small" />}
          >
            <Box align="center" justify="center">
              <Inscription size="12px" weight={500} color="textOnColor" lineHeight="18px" textAlign="center">
                All plans and payroll groups without a specific calendar assigned.
              </Inscription>
            </Box>
          </Hint>
        </Box>
      ) : (
        <Box direction="row" align="center" margin={{ top: 'spacing4' }} data-testid={`${testId}_linked-to`}>
          <Text color="textBody" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            Linked to:&nbsp;
          </Text>
          {linkedPlanNames?.length ? (
            <Box direction="row" align="center" style={{ minWidth: 0 }}>
              <ManyItemsTooltip
                items={linkedPlanNames}
                showMoreSymbolsLength={40}
                itemsTextStyles={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: theme.colors.textBody,
                  fontWeight: 700,
                }}
              />
            </Box>
          ) : (
            <Text as="span" weight={700} color="textBody">
              {linkedToText}
            </Text>
          )}
        </Box>
      )}

      {/* Frequency, Day of month, Starting date */}
      <Box direction="row" gap="spacing8" margin={{ top: 'spacing4' }} wrap>
        <Text color="textBody">
          Frequency:{' '}
          <Text as="span" weight={700} color="textBody">
            {frequency}
          </Text>
        </Text>
        <Text as="span" color="textDisabled">
          /
        </Text>
        <Text color="textBody">
          {scheduleDayLabel}:{' '}
          <Text as="span" weight={700} color="textBody">
            {scheduleDayValue}
          </Text>
        </Text>
        <Text as="span" color="textDisabled">
          /
        </Text>
        <Text color="textBody">
          Starting date:{' '}
          <Text as="span" weight={700} color="textBody">
            {startDate}
          </Text>
        </Text>
      </Box>
      <HorizontalLine margin={{ vertical: 'spacing12' }} />
      {/* Schedule Dates */}
      {scheduleYears.map((yearGroup, index) => (
        <PayrollCalendarScheduleItem
          key={yearGroup.year}
          yearGroup={yearGroup}
          isLast={index === scheduleYears.length - 1}
          testId={`${testId}_schedule`}
        />
      ))}

      {/* Edit Button */}
      <Box margin={{ top: 'spacing16' }}>
        <AppButton
          buttonType="secondary"
          size="L"
          width="100%"
          testId={`${testId}_edit-button`}
          onClick={() => {
            if (calendar.id !== undefined) {
              onEdit?.(calendar.id);
            }
          }}
        >
          Edit
        </AppButton>
      </Box>
    </Box>
  );
};

export default PayrollCalendarCard;
